import { useLDClient } from 'launchdarkly-react-client-sdk'
import React, { useCallback, useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useNavigate, useParams } from 'react-router-dom'
import { useBoolean } from 'react-use'

import { PanelDetails } from 'components/attacks/details/AttackStyles'
import Content from 'components/common/Content'
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'components/common/Tabs'
import Text from 'components/common/Text'
import Tooltip from 'components/common/Tooltip'
import TooltipContents from 'components/common/TooltipContents'
import { Header, Heading } from 'components/navigation/Header'
import { ApiKeysTable } from 'components/profile/ApiKeysTable'
import { MFA } from 'components/profile/MFA'
import ResetPassword from 'components/profile/ResetPassword'
import UserDetails from 'components/profile/UserDetails'
import UserIntegrations from 'components/profile/UserIntegrations'
import IconCounterGroup from 'components/rbac-settings/IconCounterGroup'
import useAgent from 'hooks/use-agent'
import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import theme from 'theme'
import { ApiKey } from 'types/api/api-key'
import { Company } from 'types/api/rbac/company'
import { getUserDisplayName, UserIdentity } from 'utils/user'

// memoized so it does not re-render unnecessarily
// Profile re-renders on a regular basis because of AuthenticatedRoutes' polled profile called
const ProfileContents: React.FC<{
  session: UserIdentity & {
    teams: Record<string, any> // TODO does UserIdentity need to be updated? this maybe should come from api.d.ts
  }
  company: Company
  tab: string
}> = React.memo(({ session, tab, company }) => {
  const navigate = useNavigate()
  const teams = session?.teams ?? []
  const ldClient = useLDClient()
  const [isLDReady, setIsLDReady] = useBoolean(false)

  // because the tab list depends on a feature flag we need to hook into LD here
  // so that we can know when it has loaded and is ready
  // otherwise, the redirect code later on will redirect incorrectly before the flag is loaded
  ldClient.waitUntilReady().then(
    useCallback(() => {
      setIsLDReady(true)
    }, [setIsLDReady])
  )

  const {
    data: apiKeys,
    isLoading: isLoadingApiKeys,
    hasError: hasErrorApiKeys,
    doFetch
  }: {
    data: ApiKey[]
    isLoading: boolean
    hasError: boolean
    doFetch: () => void
  } = useAgent('/apikeys', [], {})

  const tabs = [
    {
      name: 'details',
      tabTitle: 'Details',
      tabPanel: <UserDetails user={session} />,
      shouldRender: true,
      isEnabled: true
    },
    {
      name: 'security',
      tabTitle: 'Security',
      tabPanel: (
        <PanelDetails>
          <ResetPassword />
          <MFA session={session} />
        </PanelDetails>
      ),
      shouldRender: true,
      isEnabled: true
    },
    {
      name: 'integrations',
      tabTitle: 'Integrations',
      tabPanel: <UserIntegrations />,
      shouldRender: true,
      isEnabled: true
    },
    // apikeys has to be the last one because the behavior of the Tooltip disabling/enabling
    // causes the tab to be re-rendered, which changes its tab id, so it gets mixed up with the integrations tab
    // this does not occur when it is the last tab in the list
    {
      name: 'apikeys',
      tabTitle: 'API Keys',
      tabPanel: (
        <ApiKeysTable
          apiKeysLimit={5}
          doRefresh={doFetch}
          hasError={hasErrorApiKeys}
          isLoading={isLoadingApiKeys}
          keys={apiKeys ?? []}
        />
      ),
      shouldRender: company?.auth_method_prefs?.api_keys_enabled,
      isEnabled: company?.auth_method_prefs?.api_keys_enabled,
      disabledTooltipText: 'API keys have been disabled for your company'
    }
  ].filter((t) => t.shouldRender)

  const activeTabIndex = tabs.findIndex((t) => t.name === tab)

  /**
   * AuthenticatedRoutes has no knowledge of the tab configuration specific to this
   * user's privileges thus cannot perform a redirection based on this data. This check
   * sets the settings route to the first unlocked one if the current route the user
   * lands on is inaccessible.
   */
  useEffect(() => {
    // need to be sure this only executes when we have the company data and LD feature flags loaded
    // otherwise there may be false negatives in `isEnabled` that cause the page to redirect incorrectly
    if (
      company &&
      isLDReady &&
      tabs.length > 0 &&
      tabs.filter((t) => t.name === tab && t.isEnabled).length < 1
    ) {
      navigate(`/profile/${tabs[0].name}`, {
        replace: true
      })
    }
  }, [company, isLDReady, navigate, tab, tabs])

  return (
    <>
      <Text as="h1">{getUserDisplayName(session)}</Text>
      <IconCounterGroup
        counts={[
          {
            icon: 'key',
            label: 'API Key',
            count: apiKeys?.length
          },
          {
            icon: 'users',
            label: 'Team',
            count: teams.length
          }
        ]}
        css={{
          marginBottom: theme.space[3]
        }}
      />

      <Tabs
        index={activeTabIndex > -1 ? activeTabIndex : 0}
        onChange={(route) => {
          return navigate(`/profile/${tabs[route].name}`, {
            replace: true
          })
        }}
      >
        <TabList>
          {tabs.map((tab) => (
            <Tooltip
              key={tab.name}
              contents={() => {
                return (
                  <TooltipContents
                    tooltipText={
                      tab.disabledTooltipText ?? `${tab.tabTitle} is disabled`
                    }
                  />
                )
              }}
              css={{
                marginRight: 0 // reach is adding 16px here - remove it so the tabs are spaced correctly
              }}
              maxWidth="fit-content"
              tooltipEnabled={!tab.isEnabled}
              tooltipSize="small"
            >
              <div key={tab.name}>
                <Tab
                  key={tab.name}
                  data-test-tab={tab.name}
                  disabled={!tab.isEnabled}
                >
                  {tab.tabTitle}
                </Tab>
              </div>
            </Tooltip>
          ))}
        </TabList>
        <TabPanels>
          {tabs.map((tab) =>
            tab.isEnabled ? (
              <TabPanel key={tab.name}>{tab.tabPanel}</TabPanel>
            ) : (
              <TabPanel key={tab.name}>
                <div />
              </TabPanel>
            )
          )}
        </TabPanels>
      </Tabs>
    </>
  )
})

ProfileContents.displayName = 'ProfileContents'

export const Profile: React.FC = () => {
  const { tab } = useParams<{ tab: string }>()
  const { companyId, session } = useAuth()

  const { data: companyResponse, isSuccess: isSuccessCompanyResponse } =
    useGremlinQuery<Company[]>(
      `/companies/${companyId}`,
      {},
      {
        placeholderData: [],
        refetchInterval: 60_000
      }
    )

  return (
    <>
      <Helmet title="Account Settings" />
      <Header>
        <Heading>Account Settings</Heading>
      </Header>
      <Content id="Profile">
        <ProfileContents
          company={isSuccessCompanyResponse ? companyResponse[0] : undefined}
          session={session}
          tab={tab}
        />
      </Content>
    </>
  )
}
